import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import React, { useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { AuthContext } from "../../../contexts/AuthContext";
import { CurrencyProvider } from "../../../contexts/CurrencyContext";
import {
  ALL_STORES,
  isInternalUser,
  landingStore,
} from "../../../lib/dashboardAccess";
import {
  advanceBusinessReportSelector,
  changeDefaultAdvanceSelectionReport,
} from "../../../redux/slices/advance_business_report";
import { errorsSelector } from "../../../redux/slices/errors";
import { notificationsSelector } from "../../../redux/slices/notifications";
import { productDetailSelector } from "../../../redux/slices/product_detail";
import {
  getStoreGoals,
  refreshStoreAnalysis,
  setMarketplace,
  // getWeeklyInsights,
  setStore,
  storeAnalyticsSelector,
} from "../../../redux/slices/store_analytics";
import { trackEvent } from "../../../utils/analytics";
import { getForeignCurrency } from "../../../utils/currency";

import AgentRecommendations from "./components/AgentRecommendations";
import PpcAlerts from "./components/PpcAlerts";
import PpcAnalysis from "./components/PpcAnalysis";
import StoreAnalysis from "./components/StoreAnalysis";
// import StoreAnalytics from "./components/StoreAnalytics";
import StorePlanning from "./components/StorePlanning";
import StorePnL from "./components/StorePnL";
import StoreProductCompetitors from "./components/StoreProductCompetitors";
import BusinessReportAdvanced from "./components/StoreProductDetails/BusinessReportAdvanced";
import StoreProducts from "./components/StoreProducts";
import StoreProductsAnalysis from "./components/StoreProductsAnalysis";
import CurrencyPicker from "./components/shared/Currencypicker";
import MarketplacePicker from "./components/shared/Marketpicker";
import StorePicker, {
  usePortfolioStores,
  useStores,
} from "./components/shared/Storepicker";
import {
  GOALS_PORTFOLIO_STORES,
  defaultLevel1AdvancedReport,
  storesEmails,
} from "./constants";
import { TabPanel } from "./helpers";
import {
  MARKETPLACE_FLAGS,
  legacyStoreMarketplaces,
  offeredMarketplaces,
} from "./marketplaces";

function a11yProps(index) {
  return {
    value: index,
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const pagesWithMarketplacePicker = [
  "/store-products-analysis",
  "/sm-product-list",
  "/sm-pnl",
  "/ppc-analysis",
  "/sm-agents",
];
function StoreManagement() {
  const router = useLocation();
  const [searchParams] = useSearchParams();
  const Selectedstore = searchParams.get("store");
  const SelectedMarketplace = searchParams.get("marketplace");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const prevPathRef = React.useRef(router.pathname);

  const { user, method } = useContext(AuthContext);
  const isCookieAuth = method === "cookie";
  const {
    skuASINsLoading,
    productItemDetailsList,
    advanceBusinessReportLoading,
  } = useSelector(advanceBusinessReportSelector);

  const {
    productListLoading,
    storeSalesChartLoading,
    monthlyGoalsLoading,
    storePlanningsLoading,
    goalsLoading,
    productSalesLoading,
    productItemDetailsLoading,
  } = useSelector(storeAnalyticsSelector);

  const { asinNotificationsLoading } = useSelector(notificationsSelector);
  const { trackedASINsLoading } = useSelector(productDetailSelector);
  const { store: storeName } = useSelector(storeAnalyticsSelector);
  const { marketplace: marketplaceName } = useSelector(storeAnalyticsSelector);
  const errors = useSelector(errorsSelector);

  const assignStoreByEmail = useCallback((email) => {
    const storeEmails = Object.entries(storesEmails).filter(([_, e]) =>
      e.includes(email)
    );
    return storeEmails.length ? storeEmails[0][0] : null;
  }, []);

  // Load default data for advance report
  useEffect(() => {
    const selectedReport = {
      selectedStartDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
      selectedEndDate: moment().format("YYYY-MM-DD"),
      selectedLevel1: defaultLevel1AdvancedReport,
      selectedMetric: ["units", "avg_price"],
    };
    dispatch(changeDefaultAdvanceSelectionReport(selectedReport));
  }, [dispatch]);

  useEffect(() => {
    const selectedReport = { selectedLevel2: "Select" };
    if (productItemDetailsList && productItemDetailsList.length > 0) {
      selectedReport.selectedLevel2 = productItemDetailsList[0]?.sku;
    }
    dispatch(changeDefaultAdvanceSelectionReport(selectedReport));
  }, [productItemDetailsList, dispatch]);

  useEffect(() => {
    if (!user) return;
    // A ?store= deep link wins. Landing on "all" first would start an
    // all-stores fetch that can resolve after, and overwrite, the linked one.
    if (isCookieAuth && Selectedstore) return;
    const store = isCookieAuth
      ? landingStore(user)
      : user.email && assignStoreByEmail(user.email);
    if (!store) return;
    if (store === "AmazonWallDisplay") {
      navigate("/sm-planning", { state: { showCurrMonthBreakdown: true } });
      return;
    }
    dispatch(setStore(store));
  }, [
    user,
    isCookieAuth,
    Selectedstore,
    dispatch,
    assignStoreByEmail,
    navigate,
  ]);

  const tabNames = [
    "Dashboard",
    "Inventory Management",
    "Goals & KPIs",
    "Advanced Report",
    "Agents",
    "Store Analysis",
    "P&L",
  ];

  const isAtomicEmployee = isCookieAuth
    ? isInternalUser(user)
    : Boolean(user?.email?.includes("atomic-growth.com"));

  // Tab content dereferences the store unguarded. The legacy path sets one on
  // first render; the cookie path has none until the user payload arrives.
  const storeReady = storeName !== null || !isCookieAuth;

  const hideAgentsTab = isCookieAuth
    ? !isAtomicEmployee
    : (storeName?.endsWith("_client") || storeName === "all") &&
      !isAtomicEmployee;

  // ppc4us is an external partner with P&L access and no grant field of its
  // own yet, so the email carve-out still applies on both paths.
  const hidePnlTab = !isAtomicEmployee && !user?.email?.includes("ppc4us.com");

  const authorizedStores = useStores();
  const goalsStores = usePortfolioStores(GOALS_PORTFOLIO_STORES);
  const isPnlPage = router.pathname === "/sm-pnl";
  // On the cookie path P&L covers one store at a time: every granted store is
  // offered, but none is summed into an "All stores" view.
  const pnlStores = React.useMemo(
    () =>
      isCookieAuth
        ? authorizedStores.filter((s) => s.value !== ALL_STORES)
        : authorizedStores,
    [isCookieAuth, authorizedStores]
  );
  const pickerStores = isPnlPage
    ? pnlStores
    : router.pathname.includes("sm-planning")
    ? goalsStores
    : authorizedStores;
  const isEuPage =
    isPnlPage ||
    router.pathname === "/store-products-analysis" ||
    router.pathname === "/ppc-analysis";
  const offered = React.useMemo(
    () =>
      offeredMarketplaces({
        storeMarketplaces: isCookieAuth
          ? authorizedStores.find((s) => s.value === storeName)?.marketplaces ??
            []
          : legacyStoreMarketplaces(storeName),
        euPage: isEuPage,
        allowAll: isPnlPage,
      }),
    [isCookieAuth, authorizedStores, storeName, isEuPage, isPnlPage]
  );

  // Grant entries carry no is_client flag, so on the cookie path the legacy
  // computation resolves to an empty list and the gate silently opens. Fall
  // back to the internal-user check, which is the same population in practice:
  // a client-only user is never an atomic employee.
  const allStoresAreClient = React.useMemo(() => {
    if (isCookieAuth) return !isAtomicEmployee;
    if (!user?.email) return false;
    const nonAllStores = authorizedStores.filter((s) => s.value !== "all");
    return nonAllStores.length > 0 && nonAllStores.every((s) => s.is_client);
  }, [isCookieAuth, isAtomicEmployee, user, authorizedStores]);

  const [value, setValue] = React.useState(0);
  const handleChange = (_, newValue) => {
    trackEvent("tab_change", {
      tab_name: tabNames[newValue] || newValue,
      store: storeName,
    });
    setValue(newValue);
  };

  const [ppcSubTab, setPpcSubTab] = React.useState(0);
  const handlePpcSubTabChange = (_, newValue) => {
    setPpcSubTab(newValue);
  };

  useEffect(() => {
    const pathname = router.pathname;
    if (pathname.includes("sm-product-list")) {
      setValue(1);
    } else if (pathname.includes("sm-planning")) {
      setValue(2);
      dispatch(setStore("all"));
    } else if (pathname.includes("advanced-business-report")) {
      setValue(3);
    } else if (
      pathname.includes("sm-agents") ||
      pathname.includes("sm-competitors")
    ) {
      setValue(4);
    } else if (pathname.includes("store-products-analysis")) {
      setValue(5);
    } else if (pathname.includes("sm-pnl")) {
      setValue(6);
    } else if (pathname.includes("ppc-analysis")) {
      setValue(7);
    } else {
      setValue(parseInt(0));
    }
  }, [router, dispatch]);

  useEffect(() => {
    const currPath = router.pathname;
    if (currPath === "/" || currPath === "/store-management") {
      dispatch(getStoreGoals(storeName));
    }
  }, [storeName, dispatch, router]);

  const handleMarketChange = (name) => {
    trackEvent("marketplace_change", { marketplace: name, store: storeName });
    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);
    if (params.has("marketplace")) {
      params.set("marketplace", name);
      navigate(`${pathname}?${params.toString()}`, { replace: true });
    }
    dispatch(setMarketplace(name));
  };

  const handleStoreChange = (name) => {
    trackEvent("store_change", { store: name, previous_store: storeName });
    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);
    if (params.has("store")) {
      params.set("store", name);
      if (params.has("level")) {
        params.delete("level");
      }
      if (params.has("value")) {
        params.delete("value");
      }
      if (params.has("start_date")) {
        params.delete("start_date");
      }
      if (params.has("end_date")) {
        params.delete("end_date");
      }
      if (params.has("metrics")) {
        params.delete("metrics");
      }
      const newUrl = `${pathname}?${params.toString()}`;
      navigate(newUrl, { replace: true });
    }
    dispatch(setStore(name));

    // TODO: add BE support for all stores
    // if (name !== "all") {
    //   dispatch(getWeeklyInsights(name));
    // }
  };

  useEffect(() => {
    if (storeName !== null && storeName !== "all") {
      // dispatch(getWeeklyInsights(storeName));
    }
    // Goals & KPIs page must always load on all stores, regardless of the
    // store selected on the previous tab. Force "all" only when entering the
    // planning page so the user can still pick a specific store afterwards.
    const enteringPlanning =
      !prevPathRef.current.includes("sm-planning") &&
      router.pathname.includes("sm-planning");
    if (enteringPlanning) {
      dispatch(setStore("all"));
    } else if (storeName !== null || !isCookieAuth) {
      // On the cookie path the landing effect above picks the store; falling
      // back to "all" here would overwrite it on first render.
      dispatch(setStore(storeName !== null ? storeName : "all"));
    }
    prevPathRef.current = router.pathname;
    const nextMarketplace = marketplaceName !== null ? marketplaceName : "us";
    if (offered.includes(nextMarketplace)) {
      dispatch(setMarketplace(nextMarketplace));
    } else {
      dispatch(setMarketplace(offered.includes("us") ? "us" : offered[0]));
    }
  }, [
    storeName,
    marketplaceName,
    dispatch,
    router.pathname,
    isCookieAuth,
    offered,
  ]);

  useEffect(() => {
    // const pathName = router.pathname;
    if (Selectedstore) {
      dispatch(setStore(Selectedstore));
    }
    //  else if (storeName === "all") {
    //   if (pathName === "/advanced-business-report") {
    //     dispatch(setStore("atlasonix"));
    //   }
    // }
  }, [dispatch, storeName, router, Selectedstore]);

  // Apply marketplace from URL param so internal links (e.g. "View in Store
  // Analysis" on the Agents tab) can deep-link to a specific marketplace.
  // Only depends on the param itself so the user can still switch
  // marketplaces via the picker afterwards without being reverted.
  useEffect(() => {
    if (
      SelectedMarketplace &&
      Object.keys(MARKETPLACE_FLAGS).includes(SelectedMarketplace)
    ) {
      dispatch(setMarketplace(SelectedMarketplace));
    }
  }, [dispatch, SelectedMarketplace]);

  return (
    <>
      <div
        style={{
          position: "fixed",
          width: "65%",
          "z-index": "1",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        {errors
          ? errors.map((error) => (
              <Alert severity="error">
                {error.url} got error: {error.content}
              </Alert>
            ))
          : null}
      </div>
      <CurrencyProvider marketplace={marketplaceName}>
        <Box
          sx={{ width: "100%" }}
          display="flex"
          justifyContent="end"
          alignItems="center"
        >
          {router.pathname.includes("store-products-analysis") && (
            <Tooltip title="Refresh store analysis">
              <span>
                <IconButton
                  aria-label="Refresh store analysis"
                  onClick={() => {
                    trackEvent("store_analysis_refresh", {
                      store: storeName,
                      marketplace: marketplaceName,
                    });
                    dispatch(refreshStoreAnalysis());
                  }}
                  disabled={
                    !storeName ||
                    storeName === "all" ||
                    productListLoading ||
                    productSalesLoading ||
                    productItemDetailsLoading
                  }
                  sx={{
                    height: 40,
                    width: 40,
                    mr: 0.5,
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {(router.pathname === "/store-products-analysis" ||
            router.pathname === "/sm-pnl") &&
            getForeignCurrency(marketplaceName) && (
              <CurrencyPicker marketplace={marketplaceName} />
            )}
          {pagesWithMarketplacePicker.includes(router.pathname) && (
            <MarketplacePicker
              value={marketplaceName}
              onMarketSelected={handleMarketChange}
              marketplaces={offered}
              disabled={
                productListLoading ||
                storeSalesChartLoading ||
                monthlyGoalsLoading ||
                storePlanningsLoading ||
                goalsLoading ||
                trackedASINsLoading ||
                asinNotificationsLoading ||
                skuASINsLoading ||
                advanceBusinessReportLoading
              }
            />
          )}
          <StorePicker
            onStoreSelected={handleStoreChange}
            value={storeName}
            options={pickerStores}
            allowAll={isPnlPage && !isCookieAuth}
            disabled={
              productListLoading ||
              storeSalesChartLoading ||
              monthlyGoalsLoading ||
              storePlanningsLoading ||
              goalsLoading ||
              trackedASINsLoading ||
              asinNotificationsLoading ||
              skuASINsLoading ||
              advanceBusinessReportLoading
            }
          />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                label="Dashboard"
                onClick={() => navigate("/store-management")}
                {...a11yProps(0)}
              />
              <Tab
                label="Inventory Management"
                onClick={() => navigate("/sm-product-list")}
                {...a11yProps(1)}
              />
              {!allStoresAreClient && (
                <Tab
                  label="Goals & KPIs"
                  onClick={() => navigate("/sm-planning")}
                  {...a11yProps(2)}
                />
              )}
              <Tab
                label="Advanced Report"
                onClick={() => navigate("/advanced-business-report")}
                {...a11yProps(3)}
              />

              {hideAgentsTab ? (
                <Tab
                  label="Competitors"
                  onClick={() => navigate("/sm-competitors")}
                  {...a11yProps(4)}
                />
              ) : (
                <Tab
                  label="Agents"
                  onClick={() => navigate("/sm-agents")}
                  {...a11yProps(4)}
                />
              )}
              <Tab
                label="Store Analysis"
                onClick={() => navigate("/store-products-analysis")}
                {...a11yProps(5)}
              />
              {!hidePnlTab && (
                <Tab
                  label="P&L"
                  onClick={() => navigate("/sm-pnl")}
                  {...a11yProps(6)}
                />
              )}
              <Tab
                label="PPC"
                onClick={() => navigate("/ppc-analysis")}
                {...a11yProps(7)}
              />
              {/*<StyledSpan>Soon</StyledSpan>*/}
              {/*  <Tab
              label="Analytics"
              onClick={() => navigate("/sm-analytics")}
              {...a11yProps(3)}
            />
            <StyledSpan>Soon</StyledSpan>
            <Tab
              label="Snappy Settings"
              onClick={() => navigate("/sm-snappy-settings")}
              {...a11yProps(4)}
          /> */}
            </Tabs>
          </Box>
          {storeReady && (
            <>
              <TabPanel value={value} index={0}>
                <StoreAnalysis storeName={storeName} />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <StoreProducts
                  store={storeName}
                  marketplace={marketplaceName}
                />
              </TabPanel>
              {!allStoresAreClient && (
                <TabPanel value={value} index={2}>
                  <StorePlanning value={storeName} />
                </TabPanel>
              )}
              <TabPanel value={value} index={3}>
                <BusinessReportAdvanced value={storeName} />
              </TabPanel>
              {/* <TabPanel value={value} index={3}>
          <StoreAnalytics />
        </TabPanel> */}
              {/* <TabPanel value={value} index={4}>
          <StoreSnappyPlaning store={storeName} />
        </TabPanel> */}
              <TabPanel value={value} index={4}>
                {hideAgentsTab ? (
                  <StoreProductCompetitors value={storeName} />
                ) : (
                  <AgentRecommendations
                    store={storeName}
                    marketplace={marketplaceName}
                  />
                )}
              </TabPanel>
              <TabPanel value={value} index={5}>
                <StoreProductsAnalysis
                  store={storeName}
                  marketplace={marketplaceName}
                />
              </TabPanel>
              {!hidePnlTab && (
                <TabPanel value={value} index={6}>
                  <StorePnL store={storeName} marketplace={marketplaceName} />
                </TabPanel>
              )}
              <TabPanel value={value} index={7}>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                  <Tabs
                    value={ppcSubTab}
                    onChange={handlePpcSubTabChange}
                    aria-label="ppc sub tabs"
                  >
                    <Tab label="Search Term Analysis" value={0} />
                    <Tab label="Alerts" value={1} />
                  </Tabs>
                </Box>
                {ppcSubTab === 0 && (
                  <PpcAnalysis
                    store={storeName}
                    marketplace={marketplaceName}
                  />
                )}
                {ppcSubTab === 1 && (
                  <PpcAlerts store={storeName} marketplace={marketplaceName} />
                )}
              </TabPanel>
            </>
          )}
        </Box>
      </CurrencyProvider>
    </>
  );
}

export default StoreManagement;
